import { operationNameMatcher, operationNameOf, } from './ssrRetry';

/**
 * Test for a request that goes to the CMS.
 *
 * The client sends a CMS request to the `/bs` path. Each other GraphQL request
 * goes to the `/gql` path.
 *
 * This test serves the route only. It does not decide a retry. Each server-side
 * request travels to the CDN, whatever path it uses, so `ssrRetry.js` retries a
 * closed connection for each server-side query. An earlier version of this file
 * also served the retry, and that gate missed `TopNewsQuery`, which uses the
 * `/gql` path.
 *
 * Each function reads the operation name with `operationNameOf`. A direct read
 * is not safe. The name can be a getter that fails, and it can hold a value
 * that is not a string. Both forms made the earlier code throw.
 */

/** An operation with this name suffix reads a list from the CMS. */
export const LIST_NAME_SUFFIX = 'ListQuery';

/** The marketing operation that reads from the CMS. */
export const RAINBOW_NAME = 'GetMarketingData';

/**
 * Does this operation read a list?
 *
 * @param {*} operation the Apollo operation
 * @returns {boolean} true if the operation name ends with the list suffix
 */
export function checkOperationIsList(operation) {
  const name = operationNameOf(operation);

  return name !== null && name.endsWith(LIST_NAME_SUFFIX);
}

/**
 * Is this the marketing operation?
 *
 * @param {*} operation the Apollo operation
 * @returns {boolean} true if the operation has the marketing name
 */
export function checkOperationIsRainbow(operation) {
  return operationNameOf(operation) === RAINBOW_NAME;
}

/**
 * Makes the test for a request that goes to the CMS.
 *
 * @param {object} args the argument object
 * @param {boolean} args.isLocal true for a local build, which has no CDN
 * @param {*} args.brightspotUri the address of the CMS, or an empty value
 * @param {*} args.cmsQueryNames names of the operations that read from the CMS
 * @returns {Function} a test that takes an operation and gives a boolean
 */
export function createCmsBoundTest({ isLocal, brightspotUri, cmsQueryNames, }) {
  const isCmsQueryName = operationNameMatcher(cmsQueryNames);

  return operation => !isLocal
    && Boolean(brightspotUri)
    && (isCmsQueryName(operation)
      || checkOperationIsList(operation)
      || checkOperationIsRainbow(operation));
}
